/**
 * IncomingOrders — modular senior refactor
 *
 * Filen är uppdelad i mindre UI-komponenter och rena util-funktioner:
 *  - constants.js: statiska värden och tabellkolumner
 *  - hooks.js: återanvändbara hooks
 *  - viewUtils.js: ren logik för vyer/filter
 *  - components/*: presentational UI-komponenter
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  Box,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import IncomingOrdersTable from "../../components/IncomingOrders/IncomingOrdersTable";
import OrderDetailsDrawer from "../../components/IncomingOrders/OrderDetailsDrawer";
import OrderInspector from "../../components/IncomingOrders/OrderInspector";
import StatusManagerDialog from "../../components/IncomingOrders/StatusManagerDialog";
import ActionConfirmDialog from "../../components/IncomingOrders/ActionConfirmDialog";
import columnConfig from "../../components/IncomingOrders/helpers/columnConfig";
import ColumnType from "../../components/IncomingOrders/helpers/ColumnType";
import useGetIncomingOrder from "../../components/IncomingOrders/hooks/useGetIncomingOrder";
import useUpdateIncomingOrder from "../../components/IncomingOrders/hooks/useUpdateIncomingOrder";

import { CONFIRM_ACTIONS, CREATE_UPDATE_INCOMINGORDERS_GROUP, CREATE_UPDATE_INCOMINGORDERS_VIEW, DELETE_INCOMINGORDERS_VIEW, DELETE_INCOMINGORDERS_GROUP, UPDATE_INCOMING_ORDERS_TABLE_SETTINGS } from "../../graphql/mutations";
import {
  GET_INCOMINGORDER_SETTINGS,
  INCOMING_ORDERS,
} from "../../graphql/queries/IncomingOrdersContext";
import { useSnackbar } from "../../hooks";

import { processActionResponses } from "../../components/IncomingOrders/helpers/actions";
import { useSelector } from "react-redux";
import CreateViewGroupDialog from "../../components/IncomingOrders/CreateViewGroupDialog";
import IncomingOrderViewDialog from "../../components/IncomingOrders/IncomingOrderViewDialog";
//import IncomingOrdersFilterMenu from "../../components/IncomingOrders/IncomingOrdersFilterMenu";
import IncomingOrdersHeader from "../../components/IncomingOrders/IncomingOrdersHeader";
import IncomingOrdersSettingsMenu from "../../components/IncomingOrders/IncomingOrdersSettingsMenu";
import IncomingOrdersToolbar from "../../components/IncomingOrders/IncomingOrdersToolbar";
import IncomingOrdersFilterPanel from "../../components/IncomingOrders/IncomingOrdersFilterPanel";

import {
  EMPTY_FILTERS,
  TABLE_HEADERS,
  createInitialQueryParams,
} from "../../components/IncomingOrders/constants";
import { useDebouncedValue, useHoverDelay } from "./hooks";
import { useIntl } from "react-intl";
import {
  findViewById, findFirstView,
  buildHitsLabel,
  createViewFiltersPayload,
  getViewFilters,
  isRootViewTarget,
  normalizeStatusId,
} from "../../components/IncomingOrders/viewUtils";
import ORDER_ROW_TYPE_META from "../../components/IncomingOrders/helpers/types";

const IncomingOrders = () => {

  const settingsLoadedRef = useRef(false);

  const locale = useSelector(state => state.ui.locale.culture);
  const intl = useIntl();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const snackbar = useSnackbar();
  const { schedule: scheduleMenuClose, clear: clearMenuCloseTimer } = useHoverDelay(120);

  const [search, setSearch] = useState("");
  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState({
    field: columnConfig[ColumnType.SUPPLIER_ORDER_NO].sortField,
    ascending: false,
  });
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [inspectorMode, setInspectorMode] = useState("drawer");
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inspectorWidth, setInspectorWidth] = useState(600);
  const [tableDisplayMode, setTableDisplayMode] = useState("pagination");
  const [tablePage, setTablePage] = useState(0);
  const [showStatusModal, setShowStatusModal] = useState(false);

  const [filterOpen, setFilterOpen] = useState(false)

  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const activeFilterCount = useMemo(() => {
    let count = 0;

    if (filters.supplierOrderStatusIds?.length) count++;
    if (filters.orderStatus) count++;

    if (filters.productTypes?.length) count++;

    if (filters.fromDate || filters.toDate) count++;
    if (filters.deliveryDateFrom || filters.deliveryDateTo) count++;

    if (filters.orderingCompanyIds?.length) count++;

    return count;
  }, [filters]);


  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [actionsToConfirm, setActionsToConfirm] = useState([]);
  const [activeViewId, setActiveViewId] = useState("dummy1");

  /*const [groups, setgroups] = useState([]);
  const [views, setViews] = useState([]);*/

  const [viewGroups, setViewGroups] = useState({ views: [], groups: [] });


  const [editingGroup, setEditingGroup] = useState(null);
  const [sourceViewDraft, setSourceViewDraft] = useState(null);


  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [openViewGroupId, setOpenViewGroupId] = useState(null);
  const [openViewGroupAnchorEl, setOpenViewGroupAnchorEl] = useState(null);
  const [pinnedViewGroupId, setPinnedViewGroupId] = useState(null);
  const [isViewMenuHovered, setIsViewMenuHovered] = useState(false);

  const tableRef = useRef(null);
  const lastSearchedRef = useRef("");
  const skipDebounceRef = useRef(false);
  const lastPatchRef = useRef(null);
  const queryParamsRef = useRef(createInitialQueryParams());
  const debouncedSearch = useDebouncedValue(search, 400);

  const [confirmActions] = useMutation(CONFIRM_ACTIONS);
  const [createUpdateView] = useMutation(CREATE_UPDATE_INCOMINGORDERS_VIEW);
  const [createUpdateGroup] = useMutation(CREATE_UPDATE_INCOMINGORDERS_GROUP);

  const {
    getIncomingOrder,
    currentSupplierOrderId,
    refetchIncomingOrder,
    incomingOrder,
    incomingOrderLoading,
    incomingOrderError,
    updateCachedIncomingOrder,
  } = useGetIncomingOrder();

  const {
    updateIncomingOrder,
    updateResult: orderUpdateResult,
    updateLoading: orderUpdateLoading,
    updateError: orderUpdateError,
    resetUpdateResult,
  } = useUpdateIncomingOrder();

  const { data, loading, fetchMore, refetch } = useQuery(INCOMING_ORDERS, {
    variables: { ...queryParamsRef.current },
    notifyOnNetworkStatusChange: true,
    skip: !settingsLoadedRef.current
  });

  const updateQueryParams = useCallback((update) => {
    queryParamsRef.current = { ...queryParamsRef.current, ...update };
  }, []);

  const refresh = useCallback(() => {
    lastSearchedRef.current = queryParamsRef.current.searchTerm;
    refetch({ ...queryParamsRef.current }).then(({ data: refreshedData }) => {
      const incoming = refreshedData?.incomingOrdersContext?.incomingOrders;
      if (!incoming) return;
      setOrders(incoming.items);
      setTotalCount(incoming.totalResult);
    });
  }, [refetch]);

  const updateDisplayMode = useCallback((mode) => {
    setTableDisplayMode(mode);
    setSettingsAnchor(null);
    setInspectorOpen(false);
    setDrawerOpen(false);
  }, []);


  //här ska statusar och användare hämtas samtidigt
  /** FETCH */
  const [getSettings] = useLazyQuery(GET_INCOMINGORDER_SETTINGS);
  const [supplierOrderStatuses, setSupplierOrderStatuses] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);

  const [tableSettings, setTableSettings] = useState([]);
  const [currentTableSettings, setCurrentTableSettings] = useState([]);


  useEffect(() => {
    getSettings().then((res) => {
      const settings =
        res?.data?.incomingOrdersContext?.incomingOrdersSettings;

      const groups = settings?.groups ?? [];
      const views = settings?.views ?? [];
      const tSettings = settings?.tableSettings ?? [];

      setSupplierOrderStatuses(settings?.supplierOrderStatuses ?? []);
      setAvailableUsers(settings?.availableUsers ?? []);

      setViewGroups({ views, groups });
      setTableSettings(tSettings);

      settingsLoadedRef.current = true;

    });
  }, [getSettings]);

  const initializedLocaleRef = useRef(null);

  useEffect(() => {

    if (!tableSettings?.length || !locale) return;


    if (initializedLocaleRef.current === locale) return;

    const matched = tableSettings.find(
      (ts) => ts.culture === locale
    );

    const fallback = tableSettings[0];

    setCurrentTableSettings((matched ?? fallback)?.fields ?? []);

    initializedLocaleRef.current = locale;
  }, [locale, tableSettings]);


  useEffect(() => {
    const incoming = data?.incomingOrdersContext?.incomingOrders;
    if (!incoming) return;
    setOrders(incoming.items);
    setTotalCount(incoming.totalResult);
  }, [data]);

  const handleActionResponses = (actionResponses) => {
    if (!actionResponses)
      return;

    // Delay processing until the current event has completed.
    // This avoids issues with browser print dialogs.
    setTimeout(() => {
      const { doneMessages, failMessages } = processActionResponses(actionResponses, intl);

      if (doneMessages.length) {
        snackbar.success(doneMessages.join(" · "));
      }

      if (failMessages.length) {
        snackbar.error(failMessages.join(" · "));
      }
    }, 0);
  };

  useEffect(() => {
    if (!orderUpdateResult) return;

    const {
      success,
      appliedActions = [],
      result,
      actionResponses = [],
      actionsToConfirm: newActionsToConfirm = []
    } = orderUpdateResult;

    if (!success) {
      snackbar.error(intl.formatMessage({
        id: "incomingOrders.notifications.updateFailed",
      }));
      resetUpdateResult();
      return;
    }

    snackbar.success(intl.formatMessage({
      id: "incomingOrders.notifications.updated",
    }));

    handleActionResponses(actionResponses);

    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.supplierOrderId === result.supplierOrderId
          ? {
            ...order,
            supplierOrderReference: result.supplierOrderReference,
            status: result.status,
          }
          : order
      )
    );

    updateCachedIncomingOrder(result);

    if (Array.isArray(newActionsToConfirm) &&
      newActionsToConfirm.some(a => a && a !== "NONE")) {
      setActionsToConfirm(newActionsToConfirm);
      setConfirmDialogOpen(true);
    }

    resetUpdateResult();
  }, [orderUpdateResult, resetUpdateResult, snackbar, updateCachedIncomingOrder]);

  useEffect(() => {
    if (skipDebounceRef.current) {
      skipDebounceRef.current = false;
      return;
    }

    setTablePage(0);
    updateQueryParams({ offset: 0, searchTerm: debouncedSearch });
    refresh();
    // refresh depends on refetch; this effect intentionally follows only debouncedSearch changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, updateQueryParams]);

  const applyFilters = useCallback((nextFilters) => {

    setFilters(nextFilters);
    setTablePage(0);

    updateQueryParams({
      ...nextFilters,
      offset: 0,
    });

    refresh();
  }, [refresh, updateQueryParams]);

  const handleSearchKeyDown = (event) => {
    if (event.key !== "Enter") return;

    skipDebounceRef.current = true;
    if (search !== lastSearchedRef.current) {
      setTablePage(0);
      updateQueryParams({ offset: 0, searchTerm: search });
    }
    refresh();
  };

  const loadMore = () => {
    if (orders.length >= totalCount) return;

    const { offset, pageSize } = queryParamsRef.current;
    updateQueryParams({ offset: offset + pageSize });
    fetchMore({ variables: { ...queryParamsRef.current } }).then(({ data: nextData }) => {
      const incoming = nextData?.incomingOrdersContext?.incomingOrders;
      if (!incoming) return;
      setOrders((prev) => [...prev, ...incoming.items]);
      setTotalCount(incoming.totalResult);
    });
  };

  const handlePageChange = useCallback((pageIndex) => {
    const { pageSize } = queryParamsRef.current;
    setTablePage(pageIndex);
    updateQueryParams({ offset: pageIndex * pageSize });
    refresh();
  }, [refresh, updateQueryParams]);

  const handlePageSizeChange = useCallback((pageSize) => {
    setTablePage(0);
    updateQueryParams({ offset: 0, pageSize });
    refresh();
  }, [refresh, updateQueryParams]);

  const toggleType = (id) => {
    setTablePage(0);
    setFilters((current) => ({
      ...current,
      types: current.types.includes(id)
        ? current.types.filter((typeId) => typeId !== id)
        : [...current.types, id],
    }));
  };

  const handleRemoveOrderStatusFilter = () => {

    const next = {
      ...filters,
      orderStatus: null,
    };

    applyFilters(next);

  }

  const removeType = (id) => {

    const next = {
      ...filters,
      productTypes: filters.productTypes.filter((typeId) => typeId !== id),
    };

    applyFilters(next);
  };

  const toggleStatus = useCallback((id) => {
    const currentFilters = filters ?? {};
    const currentStatusIds = currentFilters.supplierOrderStatusIds ?? [];

    const nextSupplierOrderStatusIds = currentStatusIds.includes(id)
      ? currentStatusIds.filter((statusId) => statusId !== id)
      : [...currentStatusIds, id];

    applyFilters({
      ...currentFilters,
      supplierOrderStatusIds: nextSupplierOrderStatusIds,
    });
  }, [applyFilters, filters]);

  const clearStatuses = useCallback(() => {
    applyFilters({ ...filters, supplierOrderStatusIds: [] });
  }, [applyFilters, filters]);

  const clearDateFilter = () => {
    applyFilters({ ...filters, fromDate: null, toDate: null });
  };

  const handleSortChange = useCallback((field, nextAscending) => {
    setSortModel({ field, ascending: nextAscending });
    setTablePage(0);
    updateQueryParams({ offset: 0, sortField: field, sortDesc: !nextAscending });
    refresh();
  }, [refresh, updateQueryParams]);

  const handleTableDisplayModeChange = useCallback((mode) => {
    if (!mode) return;
    updateDisplayMode(mode);
    setTablePage(0);
    updateQueryParams({ offset: 0 });
    refresh();
  }, [refresh, updateDisplayMode, updateQueryParams]);

  const handleShowOrderDetails = useCallback(({ supplierOrderId }) => {
    if (!supplierOrderId) return;

    getIncomingOrder(supplierOrderId);
    if (inspectorMode === "right" && isDesktop) {
      setInspectorOpen(true);
      setDrawerOpen(false);
      return;
    }

    setDrawerOpen(true);
    setInspectorOpen(false);
  }, [getIncomingOrder, inspectorMode, isDesktop]);

  const handleUpdateOrder = useCallback(({ supplierOrderId, patch }) => {
    if (!supplierOrderId) return;
    lastPatchRef.current = patch;
    updateIncomingOrder(supplierOrderId, patch);
  }, [updateIncomingOrder]);

  const handleInspectorModeChange = (_, value) => {
    if (!value) return;
    setInspectorMode(value);
    setSettingsAnchor(null);
    setInspectorOpen(false);
    setDrawerOpen(false);
  };

  const getTypeVisual = (id) => ORDER_ROW_TYPE_META[id] ?? { label: id, icon: "" };

  const getStatusLabel = (id) =>
    supplierOrderStatuses.find((item) => normalizeStatusId(item.id) === normalizeStatusId(id))?.name ?? id;


  //****** Block som hanterar vyer ******* //
  const closeViewMenuIfUnpinned = useCallback(() => {
    if (!pinnedViewGroupId && !isViewMenuHovered) {
      setOpenViewGroupId(null);
      setOpenViewGroupAnchorEl(null);
    }
  }, [isViewMenuHovered, pinnedViewGroupId]);

  const resetViewMenu = useCallback(() => {
    clearMenuCloseTimer();
    setPinnedViewGroupId(null);
    setIsViewMenuHovered(false);
    setOpenViewGroupId(null);
    setOpenViewGroupAnchorEl(null);
  }, [clearMenuCloseTimer]);

  const handleViewGroupHover = useCallback((groupId, anchorEl) => {
    clearMenuCloseTimer();
    setOpenViewGroupId(groupId);
    setOpenViewGroupAnchorEl(anchorEl);
  }, [clearMenuCloseTimer]);

  const handleViewGroupClick = useCallback((groupId, anchorEl) => {
    clearMenuCloseTimer();
    setPinnedViewGroupId((current) => {
      const next = current === groupId ? null : groupId;
      setOpenViewGroupId(next);
      setOpenViewGroupAnchorEl(next ? anchorEl : null);
      return next;
    });
  }, [clearMenuCloseTimer]);

  const handleViewNavigationLeave = useCallback(() => {
    scheduleMenuClose(closeViewMenuIfUnpinned);
  }, [closeViewMenuIfUnpinned, scheduleMenuClose]);

  const handleViewFloatingEnter = useCallback(() => {
    clearMenuCloseTimer();
    setIsViewMenuHovered(true);
  }, [clearMenuCloseTimer]);

  const handleViewFloatingLeave = useCallback(() => {
    setIsViewMenuHovered(false);
    if (!pinnedViewGroupId) scheduleMenuClose(closeViewMenuIfUnpinned);
  }, [closeViewMenuIfUnpinned, pinnedViewGroupId, scheduleMenuClose]);

  const handleViewNavigationClose = useCallback(() => {
    resetViewMenu();
  }, [resetViewMenu]);

  const handleAddMenuOpen = useCallback((event) => {
    resetViewMenu();
    setAddMenuAnchor(event.currentTarget);
  }, [resetViewMenu]);

  const handleCreateViewClick = useCallback(() => {
    setSourceViewDraft(null);
    setAddMenuAnchor(null);
    resetViewMenu();
    setViewDialogOpen(true);
  }, [resetViewMenu]);

  const handleCreateGroupClick = () => {
    setAddMenuAnchor(null);
    setGroupDialogOpen(true);
  };

  const [updateColumnName, { loading: isRenamingColumn }] = useMutation(
    UPDATE_INCOMING_ORDERS_TABLE_SETTINGS
  );

  const handleRenameColumn = (columnName, label) => {
    updateColumnName({
      variables: {
        fields: [
          {
            culture: locale,
            field: columnName,
            label,
            settings: "{}",
          },
        ],
      },
    })
      .then(({ data: mutData }) => {
        const updated = mutData?.incomingOrders?.updateTableSetting;

        if (!updated) {
          snackbar.error("Kunde inte spara kolumnnamn");
          return;
        }

        setTableSettings(updated);

        const currentLocaleSetting = updated.find((ts) => ts.culture === locale);
        if (currentLocaleSetting) {
          setCurrentTableSettings(currentLocaleSetting.fields ?? []);
        }

        snackbar.success("Kolumnnamn uppdaterat");
      })
      .catch((err) => {
        console.error("Failed to rename column", err);
        snackbar.error("Kunde inte spara kolumnnamn");
      });
  };

  const handleViewSelect = useCallback((viewId, groupId) => {
    clearMenuCloseTimer();

    const view = findViewById(viewGroups.views, viewId);

    setActiveViewId(viewId);
    setPinnedViewGroupId(groupId ?? null);
    setOpenViewGroupId(null);
    setOpenViewGroupAnchorEl(null);
    setIsViewMenuHovered(false);
    applyFilters(getViewFilters(view));
  }, [applyFilters, clearMenuCloseTimer, viewGroups]);

  useEffect(() => {
    if (!viewGroups?.views?.length) return;

    const groups = viewGroups.groups ?? [];
    const views = viewGroups.views;

    const firstGroup = groups[0];

    const firstGroupedView = firstGroup
      ? views.find(v => v.groupId === firstGroup.id)
      : null;

    const firstUngroupedView = views.find(v => !v.groupId);

    const defaultView = firstGroupedView ?? firstUngroupedView;

    if (defaultView) {
      handleViewSelect(defaultView.id, defaultView.groupId);
    }
  }, [viewGroups, handleViewSelect]);

  const handleCreateUpdateView = useCallback(async (view) => {

    const targetGroupId = isRootViewTarget(view.groupId) ? null : view.groupId;

    const settings = JSON.stringify({
      filters: view.filters,
      columns: view.columns,
    });

    try {
      const { data: mutData } = await createUpdateView({
        variables: {
          input: {
            id: view.id ?? 0,
            name: view.name,
            groupId: targetGroupId,
            settings,
          },
        },
      });

      const result = mutData?.incomingOrders?.createUpdateView?.result;

      if (!result) {
        snackbar.error("Kunde inte spara vy");
        return;
      }

      setViewGroups((prev) => {
        const views = prev.views ?? [];

        const exists = views.some((v) => v.id === result.id);

        const updatedViews = exists
          ? views.map((v) => (v.id === result.id ? result : v))
          : [...views, result];

        return {
          ...prev,
          views: updatedViews,
        };
      });

      // Endast vid skapande
      if ((view.id ?? 0) === 0) {
        setActiveViewId(result.id);
        setPinnedViewGroupId(targetGroupId);
        applyFilters(getViewFilters(result));
      }

      setViewDialogOpen(false);
    } catch {
      snackbar.error("Något gick fel vid sparande av vy");
    }
  }, [
    applyFilters,
    createUpdateView,
    createViewFiltersPayload,
    isRootViewTarget,
    snackbar,
    viewGroups,
  ]);



  const handleEditGroupClick = useCallback((group) => {
    setEditingGroup(group);
    setGroupDialogOpen(true);
  }, []);

  const handleCloseGroupDialog = useCallback(() => {
    setGroupDialogOpen(false);
    setEditingGroup(null);
  }, []);


  const handleEditViewClick = useCallback((view) => {
    setSourceViewDraft(view);
    setViewDialogOpen(true);
  }, []);


  const handleCreateUpdateGroup = useCallback(async (group) => {
    try {
      const { data: mutData } = await createUpdateGroup({
        variables: {
          input: {
            id: group.id ?? 0,
            name: group.name,
          },
        },
      });

      const result = mutData?.incomingOrders?.createUpdateGroup?.result;

      if (!result) {
        snackbar.error("Kunde inte spara grupp");
        return;
      }

      setViewGroups((prev) => {
        const groups = prev.groups ?? [];

        const exists = groups.some((g) => g.id === result.id);

        const updatedGroups = exists
          ? groups.map((g) =>
            g.id === result.id
              ? { ...g, name: result.name }
              : g
          )
          : [...groups, { id: result.id, name: result.name }];

        return {
          ...prev,
          groups: updatedGroups,
        };
      });

      // Om det var en ny grupp, välj den
      if ((group.id ?? 0) === 0) {
        setPinnedViewGroupId(result.id);
      }

      setOpenViewGroupId(null);
      setOpenViewGroupAnchorEl(null);
      setIsViewMenuHovered(false);
      setGroupDialogOpen(false);
    } catch {
      snackbar.error("Något gick fel vid sparande av grupp");
    }
  }, [createUpdateGroup, snackbar]);


  const [deleteView] = useMutation(DELETE_INCOMINGORDERS_VIEW);
  const [deleteGroup] = useMutation(DELETE_INCOMINGORDERS_GROUP);


  const handleDeleteView = useCallback(async (viewId) => {
    if (!viewId) return;

    try {
      await deleteView({
        variables: { viewId },
      });

      setViewGroups((prev) => {
        const views = prev.views ?? [];

        return {
          ...prev,
          views: views.filter((v) => v.id !== viewId),
        };
      });

      if (activeViewId === viewId) {
        const next = findFirstView(
          viewGroups.views.filter((v) => v.id !== viewId)
        );

        if (next) {
          setActiveViewId(next.id);
          setPinnedViewGroupId(next.groupId ?? null);
          applyFilters(getViewFilters(next));
        } else {
          setActiveViewId(null);
          setPinnedViewGroupId(null);
          applyFilters(EMPTY_FILTERS);
        }
      }

      setViewDialogOpen(false);
    } catch {
      snackbar.error("Kunde inte radera vy");
    }
  }, [
    activeViewId,
    applyFilters,
    deleteView,
    snackbar,
    viewGroups,
  ]);

  const handleDeleteGroup = useCallback(async (groupId) => {
    if (!groupId) return;

    try {
      await deleteGroup({
        variables: { groupId },
      });

      setViewGroups((prev) => ({
        groups: prev.groups.filter((g) => g.id !== groupId),
        views: prev.views.map((view) =>
          view.groupId === groupId
            ? { ...view, groupId: null }
            : view
        ),
      }));

      if (pinnedViewGroupId === groupId) {
        setPinnedViewGroupId(null);
      }

      if (openViewGroupId === groupId) {
        setOpenViewGroupId(null);
        setOpenViewGroupAnchorEl(null);
      }
      setEditingGroup(null);
      setGroupDialogOpen(false);
    } catch {
      snackbar.error("Kunde inte radera grupp");
    }
  }, [
    deleteGroup,
    openViewGroupId,
    pinnedViewGroupId,
    snackbar,
  ]);

  const activeOrderView = findViewById(viewGroups.views, activeViewId);

  //****** Slut Block som hanterar vyer ******* //


  const handleConfirmActions = async (payload) => {
    setConfirmDialogOpen(false);
    setActionsToConfirm([]);

    const supplierOrderId = incomingOrder?.supplierOrderId;
    if (!supplierOrderId) return;

    if (!Array.isArray(payload) || payload.length === 0) return;

    try {
      const performActions = payload
        .filter(item => item?.type)
        .map(item => {
          const emails =
            Array.isArray(item.recipients)
              ? item.recipients
              : typeof item.recipient === "string"
                ? item.recipient
                  .split(/[;,]/)
                  .map(x => x.trim())
                  .filter(Boolean)
                : [];

          return {
            action: item.type,
            emailOverride: emails,
          };
        })
        .filter(a => a.action);

      if (performActions.length === 0) return;

      const { data } = await confirmActions({
        variables: {
          supplierOrderId,
          performActions,
        },
      });

      const actionResponses =
        data?.incomingOrders?.confirmActions?.actionResponses;

      if (!actionResponses) {
        snackbar.error("Kunde inte utföra åtgärden.");
        return;
      }

      await refetchIncomingOrder();

      handleActionResponses(actionResponses);
    } catch (err) {
      console.error("Action execution failed:", err);
      snackbar.error("Kunde inte utföra åtgärden.");
    }
  };


  const dialogCurrentFilters = useMemo(() => createViewFiltersPayload(filters), [filters]);

  const hitsLabel = buildHitsLabel(intl, totalCount, activeFilterCount);

  return (
    <Box sx={{ p: "18px 24px 32px", minHeight: "100vh", backgroundColor: "#FAFAF9" }}>

      <IncomingOrdersHeader
        hitsLabel={hitsLabel}
        debouncedSearch={debouncedSearch}
        views={viewGroups.views}
        groups={viewGroups.groups}
        activeViewId={activeViewId}
        openViewGroupId={openViewGroupId}
        openViewGroupAnchorEl={openViewGroupAnchorEl}
        pinnedViewGroupId={pinnedViewGroupId}
        addMenuAnchor={addMenuAnchor}
        onAddMenuOpen={handleAddMenuOpen}
        onAddMenuClose={() => setAddMenuAnchor(null)}
        onCreateViewClick={handleCreateViewClick}
        onCreateGroupClick={handleCreateGroupClick}
        onGroupClick={handleViewGroupClick}
        onGroupHover={handleViewGroupHover}
        onNavigationLeave={handleViewNavigationLeave}
        onFloatingEnter={handleViewFloatingEnter}
        onFloatingLeave={handleViewFloatingLeave}
        onCloseFloating={handleViewNavigationClose}
        onViewSelect={handleViewSelect}
        onEditGroupClick={handleEditGroupClick}
        onEditViewClick={handleEditViewClick}
      />

      <IncomingOrdersToolbar
        search={search}
        filters={filters}
        activeFilterCount={activeFilterCount}
        onSearchChange={setSearch}
        onSearchKeyDown={handleSearchKeyDown}
        onFilterMenuOpen={(event) => setFilterOpen(true)}
        onColumnPanelOpen={() => tableRef.current?.openColumnPanel?.()}
        onSettingsOpen={(event) => setSettingsAnchor(event.currentTarget)}
        getTypeVisual={getTypeVisual}
        getStatusLabel={getStatusLabel}
        onRemoveType={removeType}
        onRemoveSupplierStatus={toggleStatus}
        onRemoveOrderStatus={handleRemoveOrderStatusFilter}
        onClearDateFilter={clearDateFilter}
      />

      <Box sx={{ display: "flex", flexDirection: "row", alignItems: "stretch", gap: 2 }}>
        <Box
          sx={{
            flex: inspectorMode === "right" && inspectorOpen && isDesktop
              ? `1 1 calc(100% - ${inspectorWidth}px - 16px)`
              : "1 1 100%",
            minWidth: 0,
            "& > *": { borderRadius: "12px !important" },
          }}
        >
          {settingsLoadedRef.current && (
            <IncomingOrdersTable
              columnConfig={columnConfig}
              orders={orders ?? []}
              totalRowCount={totalCount ?? 0}
              loading={loading}
              headers={TABLE_HEADERS}
              ascending={sortModel.ascending}
              sortField={sortModel.field}
              onLoadMore={loadMore}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onShowOrderDetails={handleShowOrderDetails}
              onSortChange={handleSortChange}
              displayMode={tableDisplayMode}
              onDisplayModeChange={handleTableDisplayModeChange}
              page={tablePage}
              ref={tableRef}
              tableSettings={currentTableSettings}
              onRenameColumn={handleRenameColumn}
            />)}

        </Box>

        {inspectorMode === "right" && inspectorOpen && isDesktop && (
          <Box sx={{ maxWidth: 900, position: "relative" }}>
            <OrderInspector
              order={incomingOrder}
              orderError={incomingOrderError}
              orderLoading={incomingOrderLoading}
              orderUpdating={orderUpdateLoading}
              orderUpdateError={orderUpdateError}
              width={inspectorWidth}
              onWidthChange={setInspectorWidth}
              onClose={() => setInspectorOpen(false)}
              onUpdateOrder={handleUpdateOrder}
              onOpenStatusManager={() => setShowStatusModal(true)}
              availableUsers={availableUsers}
              tableSettings={tableSettings}
            />
          </Box>
        )}
      </Box>

      <OrderDetailsDrawer
        open={drawerOpen}
        order={incomingOrder}
        orderError={incomingOrderError}
        orderLoading={incomingOrderLoading}
        orderUpdating={orderUpdateLoading}
        orderUpdateError={orderUpdateError}
        onClose={() => setDrawerOpen(false)}
        onUpdateOrder={handleUpdateOrder}
        onOpenStatusManager={() => setShowStatusModal(true)}
        availableUsers={availableUsers}
        tableSettings={tableSettings}
      />

      {/*<IncomingOrdersFilterMenu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
        filters={filters}
        statuses={supplierOrderStatuses}
        onToggleType={toggleType}
        onToggleStatus={toggleStatus}
        onClearStatuses={clearStatuses}
      />*/}

      <IncomingOrdersFilterPanel
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
        supplierOrderStatuses={supplierOrderStatuses}
        onApply={(newFilters) => {
          setFilters(newFilters);
          updateQueryParams({
            ...newFilters,
            offset: 0
          });
          refresh();
        }}
        onReset={(empty) => {

          setFilters(empty);
          updateQueryParams({
            ...empty,
            offset: 0
          });
          refresh();
        }}
      />

      <IncomingOrdersSettingsMenu
        anchorEl={settingsAnchor}
        open={Boolean(settingsAnchor)}
        onClose={() => setSettingsAnchor(null)}
        theme={theme}
        inspectorMode={inspectorMode}
        tableDisplayMode={tableDisplayMode}
        onInspectorModeChange={handleInspectorModeChange}
        onDisplayModeChange={updateDisplayMode}
        onOpenStatusManager={() => {
          setShowStatusModal(true);
          setSettingsAnchor(null);
        }}
      />

      <ActionConfirmDialog
        open={confirmDialogOpen}
        order={incomingOrder}
        actions={actionsToConfirm ?? []}
        onClose={() => {
          setConfirmDialogOpen(false);
          setActionsToConfirm([]);
        }}
        onConfirm={handleConfirmActions}
      />

      <StatusManagerDialog
        statuses={supplierOrderStatuses}
        open={showStatusModal}
        onChange={setSupplierOrderStatuses}
        onClose={() => setShowStatusModal(false)}
      />

      <CreateViewGroupDialog
        open={groupDialogOpen}
        onClose={handleCloseGroupDialog}
        onCreateGroup={handleCreateUpdateGroup}
        onRenameGroup={handleCreateUpdateGroup}
        editGroup={editingGroup}
        onDeleteGroup={handleDeleteGroup}
      />

      <IncomingOrderViewDialog
        open={viewDialogOpen}
        groups={viewGroups.groups}
        productTypes={ORDER_ROW_TYPE_META}
        statuses={supplierOrderStatuses}
        sourceView={sourceViewDraft}
        activeView={activeOrderView}
        currentFilters={filters}
        onClose={() => setViewDialogOpen(false)}
        onCreateView={handleCreateUpdateView}
        onCreateGroup={handleCreateUpdateGroup}
        onRenameView={handleCreateUpdateView}
        onDeleteView={handleDeleteView}
        onOpenFilterPanel={(event) => { setFilterOpen(true); }}
        onGetColumnVisibility={() => tableRef.current?.getVisibleColumns?.()}
        onToggleColumn={(columnType) => tableRef.current?.toggleColumn?.(columnType)}
        onCreateGroupClick={handleCreateGroupClick}
        columnSummary={intl.formatMessage({ id: "incomingOrders.notifications.currentColumnsSaved" })}
      />
    </Box>
  );
};

IncomingOrders.displayName = "IncomingOrders";

export default IncomingOrders;
